import { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut, type User } from 'firebase/auth';
import { onSnapshot, doc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath, setTenantId, clearTenantId } from '@elka/firebase';

export interface WorkerContext {
  workerId: string;
  workerName: string;
  workerType?: 'office' | 'field';
  locationExempt?: boolean;
  appMode?: 'simple' | 'full';
}

const WORKER_CONTEXT_KEY = 'worker-context';
// Last tenant claim seen online. Offline, an expired ID token can't be refreshed to read
// claims, so the session reopens on this instead of locking the worker out.
const TENANT_KEY = 'worker-tenant';

/** A worker who verified online on this device (offline fallback for the claims check). */
export function hasStoredWorkerSession(): boolean {
  return (
    localStorage.getItem(WORKER_CONTEXT_KEY) !== null && localStorage.getItem(TENANT_KEY) !== null
  );
}

// Carries a one-shot auth error across the sign-out → /login navigation: useAuth
// has no shared context, so the instance that detects the problem (e.g. inside
// ProtectedRoute) and the instance that displays it (PhoneLoginForm, mounted
// fresh after the redirect) are different hook calls. Module scope is the only
// thing that survives the unmount/remount, same idea as tenant.ts's session tenant.
let pendingAuthError: string | null = null;

function readStoredWorkerContext(): WorkerContext | null {
  const contextStr = localStorage.getItem(WORKER_CONTEXT_KEY);
  if (!contextStr) return null;
  try {
    return JSON.parse(contextStr) as WorkerContext;
  } catch {
    return null;
  }
}

/**
 * Reconcile a stored worker-context against the workerId custom claim, which is
 * authoritative for the signed-in user. A disagreeing context is re-pointed at the claim
 * rather than dropped: dropping it demotes the worker to the phone-entry screen and burns
 * a fresh SMS despite a valid session, which the worker-id UUID migration would otherwise
 * inflict on every worker at once. The name is refreshed from workers/shared by the
 * live-sync effect, so a context left by a previous worker on a shared device
 * self-corrects rather than showing their name.
 */
export function reconcileWorkerContext(
  stored: WorkerContext | null,
  claimWorkerId: string | undefined
): WorkerContext | null {
  if (!stored || !claimWorkerId || stored.workerId === claimWorkerId) return stored;
  return { ...stored, workerId: claimWorkerId };
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [workerContext, setWorkerContext] = useState<WorkerContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Whether the tenant/claims resolution for the current auth state has completed.
  // Gates any tenant-scoped Firestore query so it can't race tenant resolution and
  // subscribe against the wrong path.
  const [claimsResolved, setClaimsResolved] = useState(false);
  const [authError] = useState<string | null>(() => {
    const pending = pendingAuthError;
    pendingAuthError = null;
    return pending;
  });

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (cancelled) return;
      setUser(firebaseUser);

      if (!firebaseUser) {
        setWorkerContext(null);
        localStorage.removeItem(WORKER_CONTEXT_KEY);
        localStorage.removeItem(TENANT_KEY);
        setClaimsResolved(true);
        setIsLoading(false);
        return;
      }

      // Resolve the worker's tenant from custom claims. linkWorkerByPhone mints the
      // tenantId claim server-side (that change ships separately).
      try {
        let result = await firebaseUser.getIdTokenResult();
        let tenantId =
          typeof result.claims.tenantId === 'string' && result.claims.tenantId.length > 0
            ? result.claims.tenantId
            : undefined;

        // One forced refresh to pick up a just-minted claim (stale cached token).
        if (!tenantId) {
          result = await firebaseUser.getIdTokenResult(true);
          tenantId =
            typeof result.claims.tenantId === 'string' && result.claims.tenantId.length > 0
              ? result.claims.tenantId
              : undefined;
        }

        if (cancelled) return;

        if (!tenantId) {
          // Post-cutover: every worker session must carry a tenantId claim. Without
          // one we can't safely scope Firestore/Storage reads, so sign out instead
          // of continuing unset (requireTenantId() would otherwise silently resolve
          // to the legacy '_' paths). onAuthStateChanged fires again with a null
          // user, which the branch above already handles (clears workerContext,
          // sets claimsResolved/isLoading).
          pendingAuthError = 'החשבון אינו משויך לחברה. יש להתחבר מחדש.';
          await firebaseSignOut(auth);
          return;
        }
        setTenantId(tenantId);
        localStorage.setItem(TENANT_KEY, tenantId);

        const claimWorkerId =
          typeof result.claims.workerId === 'string' ? result.claims.workerId : undefined;
        const previous = readStoredWorkerContext();
        const stored = reconcileWorkerContext(previous, claimWorkerId);
        if (stored !== previous) localStorage.setItem(WORKER_CONTEXT_KEY, JSON.stringify(stored));
        setWorkerContext(stored);
      } catch {
        if (cancelled) return;
        // Claims read failed (typically offline with an expired token) — reopen on the
        // tenant + context stored from the last online session.
        const cachedTenant = localStorage.getItem(TENANT_KEY);
        if (cachedTenant) setTenantId(cachedTenant);
        setWorkerContext(readStoredWorkerContext());
      } finally {
        if (!cancelled) {
          setClaimsResolved(true);
          setIsLoading(false);
        }
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // Live-sync workerType/locationExempt from Firestore so changes take effect without re-login.
  // Gated on claimsResolved so the tenant-scoped query never subscribes before the
  // session tenant has been resolved (which would query the wrong path).
  useEffect(() => {
    if (!claimsResolved) return;
    if (!workerContext?.workerId) return;

    const workerId = workerContext.workerId;
    const docRef = doc(db, ...splitPath(COLLECTIONS.elka.workers), 'shared');

    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (!snap.exists()) return;
      const workers = snap.data()?.workers as
        | Array<{
            id: string;
            name?: string;
            workerType?: 'office' | 'field';
            locationExempt?: boolean;
            appMode?: 'simple' | 'full';
          }>
        | undefined;
      const found = workers?.find((w) => w.id === workerId);
      if (!found) return;

      const liveType = found.workerType ?? 'field';
      const liveExempt = found.locationExempt === true;
      const liveAppMode =
        found.appMode === 'full' || found.appMode === 'simple' ? found.appMode : undefined;
      setWorkerContext((prev) => {
        if (!prev) return prev;
        const liveName = found.name ?? prev.workerName;
        if (
          prev.workerType === liveType &&
          (prev.locationExempt === true) === liveExempt &&
          prev.appMode === liveAppMode &&
          prev.workerName === liveName
        )
          return prev;
        const updated = {
          ...prev,
          workerName: liveName,
          workerType: liveType,
          locationExempt: liveExempt,
          appMode: liveAppMode,
        };
        localStorage.setItem(WORKER_CONTEXT_KEY, JSON.stringify(updated));
        return updated;
      });
    });

    return () => unsubscribe();
  }, [workerContext?.workerId, claimsResolved]);

  const setWorkerContextAndPersist = (context: WorkerContext) => {
    localStorage.setItem(WORKER_CONTEXT_KEY, JSON.stringify(context));
    setWorkerContext(context);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    localStorage.removeItem(WORKER_CONTEXT_KEY);
    localStorage.removeItem(TENANT_KEY);
    clearTenantId();
    // Full reload is LOAD-BEARING: hooks memoize queries and the session tenant lives in
    // a module-level variable, so a SPA navigation would keep streaming the previous
    // tenant's data. A hard reload resets both the React tree and the module state.
    window.location.assign('/login');
  };

  return {
    user,
    workerContext,
    isLoading,
    isAuthenticated: !!user && !!workerContext,
    isPhoneAuth: user?.providerData[0]?.providerId === 'phone',
    authError,
    signOut,
    setWorkerContextAndPersist,
  };
}
